<template>
  <div>
    <b-alert
      v-if="errorText"
      show
      variant="danger"
      class="mb-1"
    >
      {{ errorText }}
    </b-alert>

    <div
      v-if="loading && !settings.length"
      class="text-center py-2"
    >
      <b-spinner small />
    </div>

    <div
      v-for="setting in settings"
      :key="setting.key"
      class="setting-row d-flex flex-wrap align-items-center justify-content-between py-1"
    >
      <div class="setting-text pr-1">
        <div class="font-weight-bold">
          {{ setting.label }}
          <b-badge
            v-if="!setting.is_default"
            variant="light-primary"
            class="ml-50"
          >
            изменено
          </b-badge>
        </div>
        <div
          v-if="setting.description"
          class="text-muted small"
        >
          {{ setting.description }}
        </div>
        <div class="text-muted small">
          <code>{{ setting.key }}</code>
          · по умолчанию: {{ formatValue(setting, setting.default) }}
          <template v-if="hasRange(setting)">
            · от {{ setting.min }} до {{ setting.max }}
          </template>
        </div>
      </div>

      <div class="setting-control d-flex align-items-center">
        <b-spinner
          v-if="savingKey === setting.key"
          small
          class="mr-50 text-primary"
          label="Сохраняем"
        />
        <b-form-checkbox
          v-if="setting.type === 'boolean'"
          :checked="setting.value"
          switch
          size="lg"
          :disabled="savingKey === setting.key"
          @change="value => save(setting, value)"
        />

        <b-form-select
          v-else-if="setting.type === 'enum'"
          :value="setting.value"
          :options="setting.options.map(option => ({ value: option.value, text: option.label }))"
          size="sm"
          :disabled="savingKey === setting.key"
          @change="value => save(setting, value)"
        />

        <template v-else-if="setting.multiline">
          <b-form-textarea
            v-model="drafts[setting.key]"
            rows="4"
            max-rows="10"
            size="sm"
            class="setting-textarea"
            :maxlength="setting.maxLength"
            :disabled="savingKey === setting.key"
          />
          <b-button
            v-if="isDirty(setting)"
            variant="primary"
            size="sm"
            class="ml-50 align-self-start"
            :disabled="savingKey === setting.key"
            @click="saveDraft(setting)"
          >
            Сохранить
          </b-button>
        </template>

        <template v-else>
          <b-form-input
            v-model="drafts[setting.key]"
            :type="setting.type === 'string' ? 'text' : 'number'"
            :min="setting.min"
            :max="setting.max"
            :step="setting.type === 'number' ? 'any' : 1"
            size="sm"
            :class="setting.type === 'string' ? 'setting-input-wide' : 'setting-input'"
            :maxlength="setting.maxLength"
            :state="draftState(setting)"
            :disabled="savingKey === setting.key"
            @keyup.enter="saveDraft(setting)"
          />
          <b-button
            v-if="isDirty(setting)"
            variant="primary"
            size="sm"
            class="ml-50"
            :disabled="savingKey === setting.key || draftState(setting) === false"
            @click="saveDraft(setting)"
          >
            Сохранить
          </b-button>
        </template>

        <b-button
          v-if="!setting.is_default"
          v-b-tooltip.hover
          title="Вернуть значение по умолчанию"
          variant="flat-secondary"
          size="sm"
          class="ml-50"
          :disabled="savingKey === setting.key"
          @click="reset(setting)"
        >
          <feather-icon icon="RotateCcwIcon" />
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BFormCheckbox, BFormInput, BFormSelect, BFormTextarea, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import settingsApi from '@/services/settings-api.service'

/** Every setting of one category with a control by its type; the backend validates and stays the authority */
export default {
  name: 'SettingsCategoryForm',
  components: {
    BAlert, BBadge, BButton, BFormCheckbox, BFormInput, BFormSelect, BFormTextarea, BSpinner,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    category: { type: String, required: true },
  },
  data() {
    return {
      settings: [],
      drafts: {},
      loading: false,
      savingKey: null,
      errorText: '',
    }
  },
  watch: {
    category: { immediate: true, handler: 'fetch' },
  },
  methods: {
    async fetch() {
      this.loading = true
      this.errorText = ''
      try {
        const res = await settingsApi.getCategory(this.category)
        this.settings = res.data
        this.drafts = Object.fromEntries(res.data.map(setting => [setting.key, String(setting.value)]))
      } catch (e) {
        this.errorText = this.errorMessage(e)
      } finally {
        this.loading = false
      }
    },
    hasRange(setting) {
      return setting.min !== undefined && setting.max !== undefined
    },
    formatValue(setting, value) {
      if (setting.type === 'boolean') return value ? 'вкл' : 'выкл'
      if (setting.type === 'enum') return (setting.options.find(option => option.value === value) || {}).label || value
      return value
    },
    isDirty(setting) {
      return this.drafts[setting.key] !== String(setting.value)
    },
    draftValue(setting) {
      const draft = this.drafts[setting.key]
      return setting.type === 'string' ? draft : Number(draft)
    },
    draftState(setting) {
      if (!this.isDirty(setting) || setting.type === 'string') return null
      const draft = this.drafts[setting.key]
      const value = Number(draft)
      if (draft === '' || !Number.isFinite(value)) return false
      if (setting.type === 'integer' && !Number.isInteger(value)) return false
      if (setting.min !== undefined && value < setting.min) return false
      if (setting.max !== undefined && value > setting.max) return false
      return true
    },
    saveDraft(setting) {
      if (!this.isDirty(setting) || this.draftState(setting) === false) return
      this.save(setting, this.draftValue(setting))
    },
    async save(setting, value) {
      await this.mutate(setting, () => settingsApi.update(setting.key, value), `${setting.label}: ${this.formatValue(setting, value)}`)
    },
    async reset(setting) {
      await this.mutate(setting, () => settingsApi.reset(setting.key), `${setting.label}: значение по умолчанию`)
    },
    async mutate(setting, action, successText) {
      this.savingKey = setting.key
      try {
        const res = await action()
        const index = this.settings.findIndex(item => item.key === setting.key)
        this.$set(this.settings, index, res.data)
        this.$set(this.drafts, setting.key, String(res.data.value))
        this.toast('Сохранено', successText, 'success')
        this.$emit('changed', res.data)
      } catch (e) {
        this.toast('Ошибка', this.errorMessage(e), 'danger')
        await this.fetch()
      } finally {
        this.savingKey = null
      }
    },
    toast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: variant === 'success' ? 'BellIcon' : 'AlertTriangleIcon',
        },
      })
    },
    errorMessage(error) {
      const message = error?.response?.data?.message
      if (Array.isArray(message)) return message.join('; ')
      return message || 'Не удалось выполнить запрос'
    },
  },
}
</script>

<style scoped>
.setting-row + .setting-row {
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}
.setting-text {
  flex: 1 1 320px;
  min-width: 0;
}
.setting-control {
  flex: 0 0 auto;
}
.setting-input {
  width: 120px;
}
.setting-input-wide {
  width: 320px;
  max-width: 100%;
}
.setting-textarea {
  width: 420px;
  max-width: 100%;
}
</style>
